/**
 * Frontend mirror of backend/uzima_platform/dashboards.py - the two MUST be
 * kept in sync (same as farmigram keeps users/models.py and
 * config/subscriptionTiers.ts aligned). Dashboard IDs are the contract
 * between them: this file adds the presentational metadata (icon, base
 * route, description) the backend doesn't need to know about.
 */
import {
    faUserMd, faStethoscope, faHospital, faBuilding, faHeartbeat, faUserNurse,
    faHeadset, faCoins, faShieldAlt, faHandshake, faChartLine, faUsers, faCrown,
} from '@fortawesome/free-solid-svg-icons';
import type {IconDefinition} from '@fortawesome/fontawesome-svg-core';

export type DashboardId =
    | 'general_provider' | 'clinician' | 'facility' | 'institution' | 'patient' | 'individual_provider'
    | 'support_center' | 'finance_center' | 'compliance_center' | 'customer_relations'
    | 'business_development' | 'human_resources' | 'super_admin';

export type DashboardCategory = 'customer' | 'internal';

export type DashboardMeta = {
    id: DashboardId;
    label: string;
    description: string;
    icon: IconDefinition;
    basePath: string;
    category: DashboardCategory;
};

// Order here is display order on the selector page - customer dashboards
// first (in the order a growing practice would plausibly adopt them),
// internal dashboards grouped at the end since they only ever show up for
// an InternalUser.
export const DASHBOARD_REGISTRY: Record<DashboardId, DashboardMeta> = {
    general_provider: {
        id: 'general_provider', label: 'General Service Provider',
        description: 'Service center, collaborations, resources and telehealth for a general provider account.',
        icon: faHospital, basePath: '/institution/home', category: 'customer',
    },
    clinician: {
        id: 'clinician', label: 'Clinician',
        description: 'Clinical records, schedule, patients, prescriptions and collaboration workspace.',
        icon: faUserMd, basePath: '/institution/dashboard/clinician', category: 'customer',
    },
    facility: {
        id: 'facility', label: 'Facility',
        description: 'Referrals, service marketplace, staff, equipment and facility-wide reporting.',
        icon: faBuilding, basePath: '/institution/dashboard/facility', category: 'customer',
    },
    institution: {
        id: 'institution', label: 'Institution',
        description: 'Multi-facility overview - consolidated finance, workforce, and compliance across all your clinics.',
        icon: faHandshake, basePath: '/institution/org', category: 'customer',
    },
    patient: {
        id: 'patient', label: 'Patient',
        description: 'Your appointments, records, medications, treatments, billing and collaborations.',
        icon: faHeartbeat, basePath: '/patient/dashboard', category: 'customer',
    },
    individual_provider: {
        id: 'individual_provider', label: 'Individual Service Provider',
        description: 'Solo practice management - patients, records, appointments, contracts and billing, contextualised to your specialization.',
        icon: faUserNurse, basePath: '/provider/home', category: 'customer',
    },
    support_center: {
        id: 'support_center', label: 'Support Center',
        description: 'Uzima Help Desk - ticket queue, call center, omnichannel inbox and complaints.',
        icon: faHeadset, basePath: '/support', category: 'internal',
    },
    finance_center: {
        id: 'finance_center', label: 'Finance Center',
        description: 'Revenue, payments, invoicing, payouts and financial audit trail across the platform.',
        icon: faCoins, basePath: '/finance', category: 'internal',
    },
    compliance_center: {
        id: 'compliance_center', label: 'Compliance Center',
        description: 'Continuous HIPAA/GDPR/DPA compliance auditing, consent registry and violation tracking.',
        icon: faShieldAlt, basePath: '/compliance', category: 'internal',
    },
    customer_relations: {
        id: 'customer_relations', label: 'Customer Relations',
        description: 'Unified patient and partner relationship management.',
        icon: faUsers, basePath: '/crm', category: 'internal',
    },
    business_development: {
        id: 'business_development', label: 'Business Development',
        description: 'Growth pipeline, partnerships, market coverage and proposals.',
        icon: faChartLine, basePath: '/bizdev', category: 'internal',
    },
    human_resources: {
        id: 'human_resources', label: 'Human Resources',
        description: 'People directory, credentialing, training and CPD compliance.',
        icon: faUsers, basePath: '/hr', category: 'internal',
    },
    super_admin: {
        id: 'super_admin', label: 'Super Admin',
        description: 'Platform health, tenants, grants, roles and system configuration.',
        icon: faCrown, basePath: '/super-admin', category: 'internal',
    },
};

export const isInternalDashboard = (id: DashboardId) => DASHBOARD_REGISTRY[id]?.category === 'internal';

// ---------------------------------------------------------------------------
// Subscription tiers - MUST match backend/uzima_platform/dashboards.py TIERS
// ---------------------------------------------------------------------------
export type TierId = 'essential' | 'professional' | 'practice' | 'network' | 'enterprise';

export const TIERS: Record<TierId, { label: string; maxDashboards: number }> = {
    essential: {label: 'Essential', maxDashboards: 1},
    professional: {label: 'Professional', maxDashboards: 2},
    practice: {label: 'Practice', maxDashboards: 4},
    network: {label: 'Network', maxDashboards: 7},
    enterprise: {label: 'Enterprise', maxDashboards: -1},
};
